import { HttpFileServer } from '@application/http_file_server.service';
import { signal } from '@preact/signals-react';
import { Injectable } from '@visiba/instantiation';
import { EventEmitter } from '@visiba/std';
import { FileWorker } from './file_worker/mod';

export enum FileManagerServiceReadyState {
	NotReady = 'NotReady',
	Ready = 'Ready',
}

/**
 * @example
 * ```tsx
 * function MyComponent(): JSX.Element {
 *   const fileManagerService = useService(FileManagerService);
 *
 *   function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
 *     if (event.target.files != null) {
 *       fileManagerService.assignWorker(event.target.files, 'endpoint/identity');
 *
 *       queueMicrotask(() => {
 *         for (const worker of fileManagerService.peakWorkerList()) {
 *           worker.giveIdentity();
 *         }
 *       });
 *     }
 *
 *     event.target.value = '';
 *   }
 *
 *   function handleClick(): void {
 *     queueMicrotask(() => {
 *       for (const worker of fileManagerService.peakWorkerList()) {
 *         worker.upload().then(() => {
 *           fileManagerService.exhaustUploadedWorkers();
 *         });
 *       }
 *     });
 *   }
 *
 *   return (
 *     <div>
 *       <input type='file' onChange={handleFileChange} multiple />
 *       <button onClick={handleClick} />
 *     </div>
 *   );
 * }
 * ```
 */
@Injectable()
export class FileManagerService {
	public readonly readyState = signal<FileManagerServiceReadyState>(FileManagerServiceReadyState.Ready);

	public static getExtension(filename: string): string {
		const split = filename.split('.').pop();

		return split?.toLowerCase() ?? '';
	}

	public static getFileType(fileName: string): FileTypes {
		// Todo: Should this check file type?
		const extension = FileManagerService.getExtension(fileName);

		if (
			extension === 'png' ||
			extension === 'jpg' ||
			extension === 'gif' ||
			extension === 'webp' ||
			extension === 'jpeg' ||
			extension === 'tiff'
		) {
			return FileTypes.Image;
		}

		if (extension === 'doc' || extension === 'docx') {
			return FileTypes.Doc;
		}

		if (extension === 'pdf') {
			return FileTypes.Pdf;
		}

		if (extension === 'xlsx') {
			return FileTypes.Xslx;
		}

		return FileTypes.Unknown;
	}

	public static resolveMimeType(fileName: string): VisibaAcceptedMimeTypes {
		const extension = FileManagerService.getExtension(fileName);
		switch (extension) {
			case 'doc':
				return 'application/msword';
			case 'docx':
				return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			default:
				return 'application/octet-stream';
		}
	}

	public readonly events = new EventEmitter<{
		onFileWorkerUpdate: Readonly<FileWorker[]>;
	}>();

	private workers: FileWorker[] = [];

	constructor(private readonly httpFileServer: HttpFileServer) {
		// Empty
	}

	/**
	 * Assign a Worker to leverage the file uploading. This method only registers
	 * that a Worker is assigned to the file manager. The actual uploading process
	 * is managed by the Worker internally.
	 *
	 * @param file File or FileList to upload.
	 * @param identityEndpointPath An endpoint path to retrieve the file's
	 * identity. It's not static since different places may use different endpoints.
	 */
	public assignWorker(file: File, identityEndpointPath: string): void;
	public assignWorker(file: FileList, identityEndpointPath: string): void;
	public assignWorker(file: File | FileList, identityEndpointPath: string): void {
		let processingFiles: File[];
		if (file instanceof FileList) {
			processingFiles = Array.from(file);
		} else {
			processingFiles = [file];
		}

		for (const file of processingFiles) {
			const fileWorker = new FileWorker(file, identityEndpointPath, this.httpFileServer);
			this.workers.push(fileWorker);
		}

		this.events.emit('onFileWorkerUpdate', this.workers);
	}

	/**
	 * Drops a worker from the list of workers. Will cancel the uploading process
	 * if ongoing.
	 *
	 * @param id a file worker's identity.
	 */
	public dropWorker(id: string): void {
		const index = this.workers.findIndex((worker) => worker.id === id);
		if (index >= 0) {
			const worker = this.workers.splice(index, 1)[0];
			worker.drop();

			this.events.emit('onFileWorkerUpdate', this.workers);
		}

		this.updateReadyState();
	}

	public peakWorkerList(): Readonly<FileWorker[]> {
		return this.workers;
	}

	/**
	 * Returns a list of all the workers that has not been dropped.
	 */
	public peakUploadedWorkers(): Readonly<FileWorker[]> {
		const completed: FileWorker[] = [];

		for (const worker of this.workers) {
			if (worker.isUploaded()) {
				completed.push(worker);
				continue;
			}
		}

		return completed;
	}

	/**
	 *
	 *
	 * @returns List of the uploaded identifier's id.
	 */
	public peakUploadedIdentities(): string[] {
		const completed: string[] = [];
		for (const worker of this.workers) {
			const id = worker.isUploaded(true);
			if (id != null) {
				completed.push(id);
			}
		}

		return completed;
	}

	/**
	 *
	 *
	 * @returns List of the uploaded identifier's id.
	 */
	public flushUploadedIdentities(): string[] {
		const completed: string[] = [];
		const uncompleted: FileWorker[] = [];
		for (const worker of this.workers) {
			const id = worker.isUploaded(true);
			if (id != null) {
				completed.push(id);
				continue;
			}

			uncompleted.push(worker);
		}

		this.workers = uncompleted;
		if (completed.length > 0) {
			this.events.emit('onFileWorkerUpdate', this.workers);
		}

		return completed;
	}

	public updateReadyState(): void {
		const expectedUploadsLength = this.peakUploadedWorkers().length;
		const addedWorkersLength = this.peakWorkerList().length;

		this.readyState.value =
			addedWorkersLength !== 0 && expectedUploadsLength !== addedWorkersLength
				? FileManagerServiceReadyState.NotReady
				: FileManagerServiceReadyState.Ready;
	}
}

export enum FileTypes {
	Image,
	Doc,
	Pdf,
	Xslx,
	Unknown,
}

type VisibaAcceptedMimeTypes =
	| 'application/msword'
	| 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
	| 'application/octet-stream';
